import type { WebsiteLeadTouch } from '@/types/website-leads';

/**
 * Input rules of core's website lead DTOs, shared by the public forms and their
 * server actions. Core rejects any value outside them (and any unknown field)
 * with a 400, so the actions clean what they forward instead of costing a
 * visitor their request. Core re-validates everything: nothing here is a
 * security boundary.
 */
export const LEAD_LIMITS = {
  email: 320,
  name: 120,
  company: 160,
  phone: 40,
  message: 4000,
  pageUrl: 2048,
  touchValue: 255,
  touchUrl: 2048,
  turnstileToken: 2048,
  honeypot: 200,
  userAgent: 512,
  bookingUri: 512,
} as const;

/** Same patterns as the DTO decorators in core. */
const PHONE_PATTERN = /^[0-9+().\s-]{5,40}$/;
const CTA_PATTERN = /^[a-z0-9_-]{1,80}$/;
const LEAD_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;
const CONSENT_VERSION_PATTERN = /^[0-9a-z.-]{1,40}$/;
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ISO_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/;

/** Touch fields holding a campaign value or a click id (255 characters max). */
const TOUCH_VALUE_KEYS = [
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'utmTerm',
  'utmContent',
  'gclid',
  'gbraid',
  'wbraid',
  'liFatId',
  'fbclid',
  'msclkid',
] as const;

/** Touch fields holding a URL (2048 characters max). */
const TOUCH_URL_KEYS = ['referrer', 'landingPage'] as const;

/** At most `max` characters, counted as code points like core's MaxLength. */
export function truncate(value: string, max: number): string {
  const chars = Array.from(value);
  return chars.length > max ? chars.slice(0, max).join('') : value;
}

/** Trimmed and capped text, or undefined when empty or not a string. */
export function cleanText(value: unknown, max: number): string | undefined {
  if (typeof value !== 'string') return undefined;
  const clean = value.trim();
  return clean ? truncate(clean, max) : undefined;
}

export function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_PATTERN.test(value);
}

/** The short-lived token core returns with a new lead (43 base64url characters). */
export function isLeadToken(value: unknown): value is string {
  return typeof value === 'string' && LEAD_TOKEN_PATTERN.test(value);
}

/** Digits, spaces and + ( ) - . only, 5 to 40 characters. */
export function isLeadPhone(value: string): boolean {
  return PHONE_PATTERN.test(value.trim());
}

/** The ?cta= id when it is a plain slug, otherwise undefined (never an error). */
export function normalizeCta(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const clean = value.trim();
  return CTA_PATTERN.test(clean) ? clean : undefined;
}

export function normalizeConsentVersion(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  return CONSENT_VERSION_PATTERN.test(value) ? value : undefined;
}

/** An http(s) URL within `max` characters, otherwise undefined. */
export function cleanHttpUrl(value: unknown, max: number): string | undefined {
  if (typeof value !== 'string') return undefined;
  const clean = value.trim();
  if (!/^https?:\/\//.test(clean) || Array.from(clean).length > max) {
    return undefined;
  }
  return clean;
}

/**
 * Keep only the fields core's LeadTouchDto declares, capped to its limits. A
 * touch read back from browser storage may come from an older page version,
 * and one unknown key would get the whole submission rejected.
 */
export function cleanTouch(value: unknown): WebsiteLeadTouch | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const source = value as Record<string, unknown>;
  const touch: WebsiteLeadTouch = {};

  for (const key of TOUCH_VALUE_KEYS) {
    const clean = cleanText(source[key], LEAD_LIMITS.touchValue);
    if (clean) touch[key] = clean;
  }
  for (const key of TOUCH_URL_KEYS) {
    const clean = cleanText(source[key], LEAD_LIMITS.touchUrl);
    if (clean) touch[key] = clean;
  }
  if (typeof source.seenAt === 'string' && ISO_TIME_PATTERN.test(source.seenAt)) {
    touch.seenAt = source.seenAt;
  }

  return Object.keys(touch).length > 0 ? touch : undefined;
}
